<template>
  <div class="trading-notes">
    <NavBar>
      <template #left>
        <BackButton @click="onBack" />
      </template>
    </NavBar>
    <main>
      <HeadingDisplay
        class="trading-notes__header"
        title="Notas de negociação"
        subtitle="Visualize suas notas de negociação disponíveis"
      />
      <FilterSelect
        v-model="filterValue"
        class="trading-notes__filter"
        label=""
        :options="filterOptions"
        use-option-label
        @change="fetchTradingNotes"
      />
      <LoaderIndicator v-if="isLoading" with-container />
      <div v-else-if="hasError || isEmpty" class="trading-notes__feedback">
        <HelperViewContextual
          v-bind="helperViewProps"
          @click="fetchTradingNotes"
        />
      </div>
      <div v-else>
        <section
          v-for="(yearDetails, yearIndex) in availableYears"
          :key="yearIndex"
          class="trading-notes__year-section"
        >
          <div class="trading-notes__year-container">
            <BaseText
              class="trading-notes__year-title"
              size="p2"
              :color="NebraskaColors.elementPrimary"
            >
              {{ yearDetails.year }}
            </BaseText>
            <ButtonSecondary
              v-if="filterValue === EFilterTypes.VARIABLE_INCOME"
              icon="EA0230"
              label="Baixar"
              :aria-label="`Baixar aglomerado das notas de ${yearDetails.year}`"
              icon-position="right"
              :loading="isFetchingYearlyNote[Number(yearDetails.year)]"
              @click="requestYearlyNote(yearDetails)"
            />
          </div>
          <div
            v-for="(monthDetails, monthIndex) in yearDetails.months"
            :key="monthIndex"
            :class="[
              'trading-notes__accordion-wrapper',
              {
                'trading-notes__accordion-wrapper--opened':
                  openSectionsMap[monthDetails.monthIndex]
              }
            ]"
          >
            <div>
              <Accordion
                class="trading-notes__accordion"
                :should-add-text-style="false"
                @accordion-changed="
                  isOpen =>
                    handleToggleAccordion(isOpen, monthDetails.monthIndex)
                "
              >
                <template #title>
                  <BaseText size="p3" :color="NebraskaColors.elementPrimary">
                    {{ monthDetails.month }}
                  </BaseText>
                </template>
                <div class="trading-notes__accordion__content">
                  <Row
                    v-for="(report, reportIndex) in monthDetails.reports"
                    :key="reportIndex"
                    class="trading-notes__accordion__content-row"
                    has-interaction
                    @click="
                      onClickDownload(
                        yearIndex,
                        monthIndex,
                        reportIndex,
                        report
                      )
                    "
                  >
                    <template #top-left>
                      {{ report.title }}
                    </template>
                    <template #top-right>
                      <SpinnerLoader
                        v-if="report.isLoading"
                        :size="15"
                        :color="NebraskaColors.elementSecondary"
                      />
                      <BaseIcon
                        v-else
                        icon="EA0230"
                        aria-label="Download"
                        :colors="NebraskaColors.elementSecondary"
                      />
                    </template>
                  </Row>
                </div>
              </Accordion>
            </div>
          </div>
        </section>
      </div>
    </main>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router/composables';
import { notify } from '@/modules/common/helpers/notification';
import { NotificationTypes } from '@/modules/common/services/http/notification';
import { forceFileDownload } from '@/modules/common/helpers/force-download';

import {
  NavBar,
  LoaderIndicator,
  FilterSelect,
  ButtonSecondary,
  HelperViewContextual,
  HeadingDisplay,
  BaseText,
  Row,
  SpinnerLoader,
  BaseIcon
} from '@warrenbrasil/nebraska-web';
import { BackButton } from '@/modules/common/components/buttons/BackButton';
import { Accordion } from '@/modules/common/components/Accordion';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';

import { EFilterTypes } from './types';

import { trackDailyNoteDownload, trackYearlyNoteDownload } from './trackers';

import {
  getTradingNotes,
  getFixedIncomeReport,
  ITradingNoteYear,
  ITradingNoteReport
} from '@/modules/investments/services/investments';

import {
  getDailyBrokerageNote,
  getYearlyBrokerageNote
} from '@/modules/investments/services/trade';

const router = useRouter();
const route = useRoute();

const filterValue = ref('fixed-income');
const filterOptions = ref([
  { label: 'Renda Fixa', value: EFilterTypes.FIXED_INCOME },
  { label: 'Renda Variável', value: EFilterTypes.VARIABLE_INCOME }
]);

const hasError = ref(false);
const isLoading = ref(false);

const isFetchingYearlyNote = ref<Record<number, boolean>>({});
const openSectionsMap = ref<Record<number, boolean>>({});
const availableYears = ref<ITradingNoteYear[]>([]);

const helperViewProps = computed(() => {
  const errorProps = {
    'generic-error': true,
    buttonLabel: 'Tentar novamente'
  };

  const emptyProps = {
    icon: 'ED0030',
    description: 'Você não possui notas de negociação',
    context: 'neutral'
  };

  return hasError.value ? errorProps : emptyProps;
});

const isEmpty = computed(() => {
  return Boolean(availableYears.value?.length === 0);
});

watch(
  () => availableYears.value,
  sections => {
    const newIsFetchingYearlyNote: Record<number, boolean> = {};
    const newOpenSectionsMap: Record<number, boolean> = {};

    sections?.forEach(section => {
      newIsFetchingYearlyNote[Number(section.year)] = false;
      section.months.forEach(month => {
        newOpenSectionsMap[month.monthIndex] = false;
      });
    });

    openSectionsMap.value = newOpenSectionsMap;
    isFetchingYearlyNote.value = newIsFetchingYearlyNote;
  },
  { immediate: true }
);

function handleToggleAccordion(isOpen: boolean, index: number) {
  openSectionsMap.value[index] = isOpen;
}

function onBack() {
  const cameFrom = (route.query?.module as string) || 'home';

  router.push({ name: cameFrom });
}

async function fetchTradingNotes() {
  try {
    hasError.value = false;
    isLoading.value = true;

    const data = await getTradingNotes(filterValue.value);

    availableYears.value = data.availableDates;
  } catch {
    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
}

async function onClickDownload(
  yearIndex: number,
  monthIndex: number,
  reportIndex: number,
  report: ITradingNoteReport
) {
  try {
    toggleItemLoading(yearIndex, monthIndex, reportIndex);

    if (filterValue.value === EFilterTypes.FIXED_INCOME) {
      const data = await getFixedIncomeReport(report.id, report.name);

      notify({
        group: 'wrn-notifications',
        text: 'Download iniciado',
        type: NotificationTypes.success
      });

      forceFileDownload({
        data,
        type: 'application/pdf',
        fileName: `${report.title}-${report.name}.pdf`
      });
    } else {
      const { generatedURL: url } = await getDailyBrokerageNote(report.id);
      trackDailyNoteDownload(report.title);

      window.open(url, '_blank');
    }
  } catch {
    notify({
      group: 'wrn-notifications',
      text: 'Houve um problema ao iniciar o download',
      type: NotificationTypes.error
    });
  } finally {
    toggleItemLoading(yearIndex, monthIndex, reportIndex);
  }
}

async function requestYearlyNote(details: ITradingNoteYear) {
  try {
    isFetchingYearlyNote.value[Number(details.year)] = true;

    const { url } = await getYearlyBrokerageNote(details.year);

    trackYearlyNoteDownload(details.year);

    window.open(url, '_blank');
  } catch {
    notify({
      group: 'wrn-notifications',
      text: 'Houve um problema ao iniciar o download',
      type: NotificationTypes.error
    });
  } finally {
    isFetchingYearlyNote.value[Number(details.year)] = false;
  }
}

function toggleItemLoading(
  yearIndex: number,
  monthIndex: number,
  reportIndex: number
) {
  availableYears.value[yearIndex].months[monthIndex].reports[
    reportIndex
  ].isLoading =
    !availableYears.value[yearIndex].months[monthIndex].reports[reportIndex]
      .isLoading;
}

onMounted(() => {
  const cameFrom = route.query?.module as string;

  if (cameFrom === 'trade') filterValue.value = EFilterTypes.VARIABLE_INCOME;

  fetchTradingNotes();
});
</script>

<style lang="less" scoped>
.trading-notes {
  .navbar-container {
    border-bottom: none;
  }

  &__feedback,
  &__header,
  &__filter,
  &__year-container {
    max-width: 1311px;
    margin: auto;
    padding: 0 @size-spacing-x400;

    .from-md({
      padding: 0 @size-spacing-x800;
    });
  }

  &__feedback {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__header {
    margin-top: @size-spacing-x800;
    margin-bottom: @size-spacing-x800;
  }

  &__filter {
    margin-bottom: @size-spacing-x500;
  }

  &__year-section {
    margin-bottom: @size-spacing-x800;
  }

  &__year-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: @size-spacing-x400;
  }

  &__year-title {
    font-weight: bold;
  }

  &__accordion {
    max-width: 1215px;
    margin: 0 auto;

    :deep(button) {
      padding: @size-spacing-x400 0 !important;
    }

    &-wrapper {
      width: 100%;
      padding: 0 @size-spacing-x400;

      .from-md({
        padding: 0 @size-spacing-x800;
      });

      &--opened {
        background-color: @background-primary;
      }
    }

    &__content {
      padding-bottom: @size-spacing-x600;
    }

    &__content-row {
      background-color: @background-primary;
    }
  }
}
</style>
